import classNames from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoChevronDown, IoChevronUp, IoSearch } from 'react-icons/io5';
import { FilterValue } from 'react-table';

import MButton from 'views/Buttons/MButton/MButton';
import MIcon from 'views/MIcon/MIcon';

import { ITableFilterOptions } from './MFilter.model';
import classes from './MFilter.module.scss';

type Props = {
  name: string;
  displayLabel?: string;
  showChevron?: boolean;
  isOpenedFilter: boolean;
  options: ITableFilterOptions[];
  containerRef: React.RefObject<HTMLDivElement>;
  dropdownRef: React.RefObject<HTMLDivElement>;
  contentStyle: React.CSSProperties;
  value: FilterValue;
  bottomToTop?: boolean;
  searchViaButton?: boolean;
  fixedOptions: boolean;
  showSelectAll: boolean;
  isAllSelected: boolean;
  isFiltered: boolean;
  onToggleAll: () => void;
  onSelect: (index: number) => void;
  onFilterOptions: (event: React.FormEvent<HTMLInputElement>) => void;
  enterValue: (value: string) => void;
  onOpenFilter: () => void;
  isChecked: (id: string) => boolean;
};

const MFilterView = (props: React.PropsWithChildren<Props>): JSX.Element => {
  const [inputValue, setInputValue] = useState<string>('');

  const { t } = useTranslation();

  return (
    <div ref={props.containerRef} className={classNames(classes['container'], { [classes['container__input']]: props.showChevron })}>
      <span className={classes['btnFilter']} onClick={props.onOpenFilter}>
        <span className={classes['btnFilter__label']}>{props.displayLabel || t(props.name)}</span>
        {props.showChevron ? (
          <MIcon className={classes['btnFilter__chevron']} name={props.isOpenedFilter ? IoChevronUp : IoChevronDown} size="s"></MIcon>
        ) : null}
      </span>
      {props.isOpenedFilter ? (
        <div
          ref={props.dropdownRef}
          style={props.contentStyle}
          className={classNames(
            `${classes['content']} ${classes['content--show']} ${props.bottomToTop ? classes['content--bottomToTop'] : ''}`,
          )}
        >
          <div className={classNames(`${classes['triangle']} ${props.bottomToTop ? classes['triangle--bottomToTop'] : ''}`)}></div>
          <div className={classNames(`${classes['filter']} ${props.bottomToTop ? classes['filter--bottomToTop'] : ''}`)}>
            <span className={`${classes['filter__title']} ${props.options.length ? classes['filter__title--line'] : ''}`}>
              <MIcon className={classes['icon']} name={IoSearch} size="m" viewBox="612 612"></MIcon>
              {props.searchViaButton ? (
                <div className={classes['searchViaButton']}>
                  <input
                    placeholder={props.displayLabel || t(props.name)}
                    onChange={(value: React.ChangeEvent<HTMLInputElement>) => setInputValue(value.target.value)}
                    className={classes['filterOptions']}
                  />
                  <MButton disabled={!inputValue} onClick={() => props.enterValue(inputValue)}>
                    {t('common.enter')}
                  </MButton>
                </div>
              ) : (
                <input placeholder={props.displayLabel || t(props.name)} onChange={props.onFilterOptions} className={classes['filterOptions']} />
              )}
            </span>
            <div className={classes['filter__options']}>
              {props.showSelectAll && props.options.length ? (
                <div
                  className={classNames(classes['option'], classes['option--selectAll'])}
                  onClick={props.isFiltered ? undefined : props.onToggleAll}
                  style={props.isFiltered ? { pointerEvents: 'none', opacity: 0.5 } : {}}
                >
                  <input
                    type="checkbox"
                    className={classes['option__checkbox']}
                    checked={props.isAllSelected}
                    onChange={props.onToggleAll}
                    disabled={props.isFiltered}
                  />
                  <span className={classes['option__title']}>{props.isAllSelected ? t('common.unselectAll') : t('common.selectAll')}</span>
                </div>
              ) : null}
              {props.options.map((option: ITableFilterOptions, index: number) => (
                <div key={`${option.id}_${index}`} className={classes['option']} onClick={() => props.onSelect(index)}>
                  <input
                    type="checkbox"
                    className={classes['option__checkbox']}
                    checked={!!props.isChecked(option.id)}
                    onChange={() => props.onSelect(index)}
                  />
                  {option.icon ? <MIcon name={option.icon} /> : null}
                  {/* If the label contains a '.', it indicates that the string is a translation path.*/}
                  <span className={classes['option__title']}>{props.fixedOptions ? t(option.label) : option.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

MFilterView.displayName = 'MFilterView';

export default MFilterView;
